import { webAppAPI } from 'api/web-app';
import { ObjectRef } from 'proto/github.com/solo-io/dev-portal/api/dev-portal/v1beta1/common/common_pb';
import {
  APIProductDetails,
  APIProductVersionDetails,
  GRPCInvokeRequest,
  GRPCInvokeResponse
} from 'proto/web_app_grpc_pb';
import * as React from 'react';
import { createSelector } from 'reselect';
import { Selector, State, SwaggerComponent, System } from './SwaggerComponent';
import { APIType } from 'proto/github.com/solo-io/dev-portal/api/dev-portal/v1beta1/apiproduct_pb';

interface SwaggerHolderProps {
  swaggerJSON: object;
  disableTryItOut: boolean;
  portalUrlPrefix: string;
  apiProduct: APIProductDetails.AsObject;
  environmentRef: ObjectRef.AsObject;
  versionDetails?: APIProductVersionDetails.AsObject;
}

// the arbitrarily chosen mapping from HTTP verbs to gRPC method types:
//   unary -> get
//   client streaming -> post
//   server streaming -> put
//   bidirectional steaming -> patch
enum MethodMapping {
  CLIENT_STREAM = 'post',
  UNARY = 'get',
  SERVER_STREAM = 'put',
  BIDIRECTIONAL_STREAM = 'patch'
}

enum Status {
  'OK',
  'CANCELLED',
  'UNKNOWN',
  'INVALID_ARGUMENT',
  'DEADLINE_EXCEEDED',
  'NOT_FOUND',
  'ALREADY_EXISTS',
  'PERMISSION_DENIED',
  'RESOURCE_EXHAUSTED',
  'FAILED_PRECONDITION',
  'ABORTED',
  'OUT_OF_RANGE',
  'UNIMPLEMENTED',
  'INTERNAL',
  'UNAVAILABLE',
  'DATA_LOSS',
  'UNAUTHENTICATED'
}

export const SwaggerHolder = ({
  swaggerJSON,
  disableTryItOut,
  portalUrlPrefix,
  versionDetails,
  apiProduct,
  environmentRef
}: SwaggerHolderProps) => {
  const [grpcResponse, setGrpcResponse] = React.useState<GRPCInvokeResponse.AsObject>();
  const [grpcRequest, setGrpcRequest] = React.useState<GRPCInvokeRequest.AsObject>({
    apiProductRef: { name: apiProduct?.metadata?.name!, namespace: apiProduct?.metadata?.namespace! },
    environmentRef,
    apiKey: '',
    argumentJson: '',
    methodName: '',
    // TODO: handle OAuth
    oauthAccessToken: '',
    serviceDomain: '',
    serviceName: '',
    version: versionDetails?.name!
  });

  React.useEffect(() => {
    if (!grpcRequest.methodName) {
      setGrpcResponse(undefined);
      return;
    }
    const getResponse = async () => {
      let res = await webAppAPI.invokeGrpcMethod(grpcRequest);
      setGrpcResponse(res);
    };
    getResponse();
  }, [grpcRequest.methodName]);

  // Plugins to extend SwaggerUI to be used with gRPC:
  // These are kept at the component scope because they use state variables
  function SwaggerPlugins(system: System) {
    const SET_GRPC_RESPONSE = 'set_grpc_response'; // strings just need to be uniuqe... see below
    // Tools
    const fromJS = system.Im.fromJS; // needed below

    return {
      statePlugins: {
        // has to be called `spec` in order to override the `spec` namespace
        spec: {
          wrapSelectors: {
            // This overrides the component that shows responses from the Try It Out feature
            // TODO: modify to support showing grpcurl in curl request component
            allowTryItOutFor: (originalSelector: Selector, system: System) => (
              state: State,
              path: string,
              method: string
            ) => {
              // args are [path,method] so we check that the only method we allow is get (unary)
              if (disableTryItOut) return false;
              if (method !== 'get' && versionDetails?.apiType === APIType.GRPC) return false;
              return true;
            }
          },
          actions: {
            setGrpcResponse: (args: any) => ({ type: SET_GRPC_RESPONSE, payload: args }) // Synchronous action must return an object for the reducer to handle
          },
          reducers: {
            [SET_GRPC_RESPONSE]: (state: State, action: any) => {
              // Take a state (which is immutable) and an action (see synchronous actions) and return a new state
              return state.set('grpcResponse', fromJS(action.payload)); // we're updating the Immutable state object... we need to convert vanilla objects into an immutable type (fromJS)
              // See immutable about how to modify the state
              // PS: you're only working with the state under the namespace, in this case "someNamespace". So you can do what you want, without worrying about /other/ namespaces
            }
          },
          selectors: {
            getGrpcResponse: createSelector(
              // see [reselect#createSelector](https://github.com/reactjs/reselect#createselectorinputselectors--inputselectors-resultfunc)
              state => state, // This is a requirement... because we `bind` selectors, we don't want to bind to any particular state (which is an immutable value) so we bind to a function, which returns the current state
              (state: State) => state.get('grpcResponse') // return the whatever "something" points to
            )
          },
          wrapActions: {
            setRequest: (oriAction: any, system: any) => async (...args: any) => {
              if (versionDetails?.apiType === APIType.GRPC) {
                if (args) {
                  let [path, verb, req] = args;
                  let { body, url, headers } = req;
                  let [scheme, _, domain, packageService, methodName] = url.split('/');
                  setGrpcRequest({
                    ...grpcRequest,
                    apiKey: !!headers ? headers['api-key'] ?? '' : '',
                    argumentJson: JSON.stringify(body),
                    methodName,
                    serviceDomain: `${domain}`,
                    serviceName: packageService
                  });
                  let res = await webAppAPI.invokeGrpcMethod({
                    ...grpcRequest,
                    apiKey: !!headers ? headers['api-key'] ?? '' : '',
                    argumentJson: JSON.stringify(body),
                    methodName,
                    serviceDomain: `${domain}`,
                    serviceName: packageService
                  });

                  system.specActions.setGrpcResponse(res);
                  setGrpcResponse(res);
                }
              }
              return oriAction(...args);
            }
          }
        }
      },
      wrapComponents: {
        VersionStamp: (Original: React.FC, system: System) => (props: any) => {
          if (versionDetails?.apiType === APIType.GRPC) {
            return null;
          }
          return <Original {...props} />;
        },
        curl: (Original: React.FC, system: System) => (props: any) => {
          if (versionDetails?.apiType === APIType.GRPC) {
            return null;
          }
          return <Original {...props} />;
        },
        OperationSummaryMethod: (Original: React.FC, system: System) => (props: any) => {
          if (versionDetails?.apiType === APIType.GRPC) {
            if (props.method === MethodMapping.CLIENT_STREAM) {
              return (
                <div className='opblock-summary opblock-summary-post'>
                  <span className='py-2 opblock-summary-method'>Client Stream</span>
                </div>
              );
            } else if (props.method === MethodMapping.UNARY) {
              return (
                <div className='opblock-summary opblock-summary-get'>
                  <span className='py-2 opblock-summary-method'>Unary</span>
                </div>
              );
            } else if (props.method === MethodMapping.SERVER_STREAM) {
              return (
                <div className='opblock-summary opblock-summary-put'>
                  <span className='py-2 opblock-summary-method'>Server Stream</span>
                </div>
              );
            } else if (props.method === MethodMapping.BIDIRECTIONAL_STREAM) {
              return (
                <div className='opblock-summary opblock-summary-put'>
                  <span className='py-2 opblock-summary-method'>Bidirectional Stream</span>
                </div>
              );
            }
          }
          return <Original {...props} />;
        },
        liveResponse: (Original: React.FC, system: System) => (props: any) => {
          if (versionDetails?.apiType === APIType.GRPC) {
            system.specActions.updateLoadingStatus('success');

            const { response, getComponent, getConfigs, displayRequestDuration, specSelectors, path, method } = props;

            const { showMutatedRequest } = getConfigs();

            const curlRequest = showMutatedRequest
              ? specSelectors.mutatedRequestFor(path, method)
              : specSelectors.requestFor(path, method);
            const status = Status[system.specSelectors.getGrpcResponse().toJS()?.statusCode ?? Status.UNKNOWN];
            const url = curlRequest?.get('url') || '';

            const headers = response.get('headers').toJS();
            const ResponseBody = getComponent('responseBody');

            return (
              <div>
                <div>
                  {url && (
                    <div>
                      <h4>Request URL</h4>
                      <div className='request-url'>
                        <pre className='microlight'>{url}</pre>
                      </div>
                    </div>
                  )}
                  <h4>Server response</h4>
                  <table className='responses-table live-responses-table'>
                    <thead>
                      <tr className='responses-header'>
                        <td className='col_header response-col_status'>Code</td>
                        <td className='col_header response-col_description'>Details</td>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className='response'>
                        <td className='response-col_status'>{status}</td>
                        <td className='response-col_description'>
                          <ResponseBody
                            content={system.specSelectors.getGrpcResponse().toJS()?.data ?? ''}
                            contentType={'json'}
                            url={url}
                            headers={headers}
                            getConfigs={getConfigs}
                            getComponent={getComponent}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          }
          return <Original {...props} />;
        }
      }
    };
  }

  return (
    <div className='p-1 mt-4 bg-white border border-gray-400 rounded-lg'>
      <SwaggerComponent
        isGrpc={versionDetails?.apiType === APIType.GRPC}
        spec={swaggerJSON}
        specId={`${versionDetails?.name}-${environmentRef?.name}-${environmentRef?.namespace}`}
        plugins={[SwaggerPlugins]}
        oauth2RedirectUrl={!!portalUrlPrefix ? portalUrlPrefix + '/oauth-redirect' : ''}
        requestInterceptor={async request => {
          let { body, url, headers } = request;
          let [scheme, _, domain, packageService, methodName] = url.split('/');
          if (request && versionDetails?.apiType === APIType.GRPC) {
            setGrpcRequest({
              ...grpcRequest,
              apiKey: !!headers ? headers['api-key'] ?? '' : '',
              argumentJson: body,
              methodName,
              serviceDomain: `${domain}`,
              serviceName: packageService
            });
            let res = await webAppAPI.invokeGrpcMethod({
              ...grpcRequest,
              apiKey: !!headers ? headers['api-key'] ?? '' : '',
              argumentJson: body,
              methodName,
              serviceDomain: `${domain}`,
              serviceName: packageService
            });
            setGrpcResponse(res);
          }

          return new Promise((resolve, reject) => {
            resolve(request);
          });
        }}
        responseInterceptor={async res => {
          return new Promise((resolve, reject) => {
            // set to the startup value again
            setGrpcRequest({
              apiProductRef: { name: apiProduct?.metadata?.name!, namespace: apiProduct?.metadata?.namespace! },
              environmentRef,
              apiKey: '',
              argumentJson: '',
              methodName: '',
              // TODO: handle OAuth
              oauthAccessToken: '',
              serviceDomain: '',
              serviceName: '',
              version: versionDetails?.name!
            });
            setGrpcResponse(undefined);

            resolve(res);
          });
        }}
      />
    </div>
  );
};
